import { ldb } from '@codesuma/baseline'
import utils from '../utils'
import supabase from './supabase'
import listsService from './lists'

// A list's item order (item ids, top first). The copy that counts lives on the
// list row (`lists.item_order`, migration 012) so every member and every device
// shows the same sequence; localStorage mirrors it here for an instant,
// offline-safe paint before any server answer arrives.
const key = (listId: string) => 'order:' + listId

const get = (listId: string): string[] => ldb.get(key(listId)) || []

// Local mirror only — the sequence the page recomputes on every render (new
// items lead, deleted ids drop out). Not worth a server round trip: every
// device derives the same result from the same rule.
const setLocal = (listId: string, ids: string[]): void => ldb.set(key(listId), ids)

// Adopt an order that came from the server (the list fetch, or another member's
// drag arriving over Realtime). Ids we don't have are ignored by applyOrder.
//
// The server's array is only rewritten by a deliberate drag, so it is normally a
// *subset* of the local mirror — it knows nothing of the items added since.
// Replacing the mirror with it therefore dropped those ids out of the sequence,
// and an unplaced id leads (sortByOrder), so a handful of settled items jumped
// to the top. Every Realtime re-subscribe refetches, so this fired after any
// backgrounding or network blip: the "items jumped after a while" report.
//
// Merging instead: the server decides the order of the ids it knows, and each
// local-only id stays with the id it sits in front of locally. Ids trailing the
// last known one keep the end.
const adopt = (listId: string, ids: unknown): void => {
    if (!Array.isArray(ids)) return
    const server = ids as string[]
    const known = new Set(server)
    const before = new Map<string, string[]>() // known id -> local-only ids ahead of it
    const tail: string[] = []
    let buffer: string[] = []
    for (const id of get(listId)) {
        if (known.has(id)) {
            before.set(id, buffer)
            buffer = []
        } else {
            buffer.push(id)
        }
    }
    tail.push(...buffer)
    const merged = server.flatMap(id => [...(before.get(id) || []), id])
    setLocal(listId, [...merged, ...tail])
}

// Persist a user's drag: locally first (instant, offline-safe), then onto the
// list row so the other members' devices get it live. temp_ ids are dropped —
// the server has never seen them, and they lead locally until they sync.
const push = async (listId: string, ids: string[]): Promise<void> => {
    setLocal(listId, ids)
    // No session means nothing of this user's is on the server yet, and an
    // unsynced list (e.g. an untouched starter) has no row to update — either
    // way the next drag after it syncs carries the order up.
    if (!supabase.auth.isAuthenticated()) return
    if (listsService.getCached(listId)?.synced === false) return
    try {
        const order = ids.filter(id => !id.startsWith('temp_'))
        await utils.http.put(`/api/v1/lists/list/${listId}/order`, { order })
    } catch (err) {
        console.warn('Failed to save item order on server:', err)
    }
}

export default {
    get,
    setLocal,
    adopt,
    push,
}
